import React, { useEffect, useState } from 'react';
import KitModal from '../../../components/UI/KitModal';
import { Box, Button, CircularProgress } from '@mui/material';
import { FluxService } from '../../../service/FluxService';
import NodesPreview from './NodesPreview';

type Props = {
  show: boolean;
  onHide: () => void;
  nodes: any;
  flow: any;
};

const PreviewModal = ({ show, onHide, nodes, flow }: Props) => {
  const [inputs, setInputs] = useState<any>([]);
  const [outputs, setOutputs] = useState<any>([]);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    const flowInputs = flow?.nodes
      .filter((node: any) => node.type === 'varInputNode')
      ?.map((node: any) => ({
        inputId: node?.id,
        inputText: '',
      }));

    const flowOutputs = flow?.nodes
      .filter((node: any) => node.type === 'outputObjectNode')
      ?.map((node: any) => ({
        outputId: node?.id,
        text: '',
      }));

    setInputs(flowInputs);
    setOutputs(flowOutputs);
  }, [flow]);

  const calculatePositions = (nodes: any) => {
    const sortedNodes = nodes.sort(
      (a: any, b: any) => a.position.y - b.position.y,
    );
    const minY = Math.min(...sortedNodes.map((node: any) => node.position.y));

    const adjustedNodes = sortedNodes.map((node: any) => {
      return {
        ...node,
        position: {
          x: node.position.x < 0 ? 0 : node.position.x,
          y: node.position.y - minY < 0 ? 0 : node.position.y - minY,
        },
      };
    });

    const groupedNodes: any[] = [];
    const range = 70;
    adjustedNodes.forEach((node: any) => {
      const group = groupedNodes.find((g: any) =>
        g.some((n: any) => Math.abs(n.position.y - node.position.y) <= range),
      );
      if (group) {
        group.push(node);
      } else {
        groupedNodes.push([node]);
      }
    });

    let currentY = 0;
    const marginY = 20;
    const marginX = 10;

    const finalNodes: any[] = [];
    groupedNodes.forEach(group => {
      let currentX = 0;
      group.forEach((node: any) => {
        finalNodes.push({
          ...node,
          position: {
            x: currentX,
            y: currentY,
          },
        });
        currentX += (node?.width || 100) + marginX;
      });
      const maxHeight = Math.max(
        ...group.map((node: any) => node?.height || 100),
      );
      currentY += maxHeight + marginY;
    });

    // sort groupedNodes by position X
    const sorted = groupedNodes.map(newGroup =>
      newGroup.sort((a: any, b: any) => a.position.x - b.position.x),
    );

    // Step 5: Sort flexGroups by X position within their rows

    return { finalNodes, groupedNodes: sorted };
  };

  const onSubmit = async () => {
    setFormLoading(true);
    try {
      const response = await FluxService.FluxApi(flow?.id!, {
        variableInputs: inputs,
      });

      // set new output

      setOutputs(response?.data?.message);
    } catch (error) {
      console.error(error);
    } finally {
      setFormLoading(false);
    }
  };

  const ref = React.useRef(null);
  const { finalNodes: adjustedNodes, groupedNodes } = calculatePositions(nodes);

  return (
    <KitModal
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '1500px',
          background: '#F2F4F7',
          overflow: formLoading ? 'hidden' : '',
        },

        '& .MuiDialogContent-root': {
          background: '#F2F4F7',
          // scroll hidden
          overflow: formLoading ? 'hidden' : '',
        },
      }}
      fullWidth
      show={show}
      onHide={onHide}
    >
      <Box
        sx={{
          maxWidth: '1064px',
          margin: '0 auto',
        }}
        ref={ref}
      >
        {formLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              width: '100vw',
              position: 'fixed',
              top: 0,
              left: 0,
              zIndex: 1000,
              backgroundColor: 'rgba(255,255,255,0.5)',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {/* {adjustedNodes?.map((nd: any, index) => {
          return <Nodes key={index} nd={nd} />;
        })} */}

        <Box>
          {groupedNodes?.map((group: any, index) => {
            if (group.length === 1) {
              return (
                <NodesPreview
                  key={index}
                  inputs={inputs}
                  outputs={outputs}
                  setInputs={setInputs}
                  setOutputs={setOutputs}
                  onSubmit={onSubmit}
                  nd={group[0]}
                />
              );
            }

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: '16px',
                  // justifyContent: 'space-between',
                  flexWrap: 'wrap',

                  w: '100%',

                  '@media (max-width: 600px)': {
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  },
                }}
              >
                {group.map((nd: any, index: any) => {
                  return (
                    <NodesPreview
                      key={index}
                      nd={nd}
                      inputs={inputs}
                      outputs={outputs}
                      setInputs={setInputs}
                      setOutputs={setOutputs}
                      onSubmit={onSubmit}
                    />
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </KitModal>
  );
};

export default PreviewModal;
